import { useState, useEffect, useCallback, useRef } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { BookOpen, Loader2, Check, RefreshCw, ChevronDown, Megaphone, Flag, Sparkles, RefreshCcw } from "lucide-react";

const SIDE = {
  a: { label: "text-view-a", dot: "bg-view-a", pill: "text-view-a bg-view-a-light", btn: "bg-view-a",
       edge: "border-view-a", grad: "from-[#7B2CBF] to-[#5a1f8f]", badge: "bg-view-a", icon: Megaphone,
       ring: "ring-view-a", soft: "bg-view-a-light" },
  b: { label: "text-view-b", dot: "bg-view-b", pill: "text-view-b bg-view-b-light", btn: "bg-view-b",
       edge: "border-view-b", grad: "from-[#FFC300] to-[#E0A800]", badge: "bg-view-b", icon: Flag,
       ring: "ring-view-b", soft: "bg-view-b-light" },
};

function parseEssay(text) {
  const lines = (text || "").split("\n").map((l) => l.trim()).filter(Boolean);
  let hook = "";
  const bullets = [];
  for (const l of lines) {
    if (/^[-•*]\s+/.test(l)) {
      const body = l.replace(/^[-•*]\s+/, "");
      const m = body.match(/^\*\*(.+?)\*\*[:：]?\s*(.*)$/);
      if (m) bullets.push({ lead: m[1].trim(), text: m[2].trim() });
      else bullets.push({ lead: "", text: body.replace(/\*\*/g, "") });
    } else if (!hook) hook = l.replace(/\*\*/g, "");
    else bullets.push({ lead: "", text: l.replace(/\*\*/g, "") });
  }
  return { hook, bullets };
}

function SideInfographic({ side, label, hook, bullets, image }) {
  const s = SIDE[side];
  const Icon = s.icon;
  const pillars = bullets.slice(0, 3);
  const headerStyle = image
    ? { backgroundImage: `linear-gradient(to bottom right, rgba(0,0,0,0.4), rgba(0,0,0,0.62)), url(${image})`, backgroundSize: "cover", backgroundPosition: "center" }
    : undefined;
  return (
    <div className="rounded-xl overflow-hidden mb-4 border border-stone-200 dark:border-[#262B38] shadow-sm" data-testid={`infographic-${side}`}>
      <div className={`relative p-4 text-white ${image ? "min-h-[150px] flex flex-col justify-end" : `bg-gradient-to-br ${s.grad}`}`} style={headerStyle}>
        {!image && <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "radial-gradient(circle at 80% 10%, rgba(255,255,255,0.5), transparent 40%)" }} />}
        <div className="relative flex items-center gap-2 mb-1">
          <span className="w-8 h-8 rounded-lg bg-white/25 backdrop-blur-sm flex items-center justify-center"><Icon className="w-4 h-4" /></span>
          <span className="text-sm font-bold uppercase tracking-wider drop-shadow">{label}</span>
        </div>
        {hook && <p className="relative text-[13px] leading-snug text-white/95 font-medium drop-shadow">{hook}</p>}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 p-3 bg-white">
        {pillars.map((p, i) => (
          <div key={`pil-${i}`} className="flex items-start gap-2">
            <span className={`shrink-0 w-6 h-6 rounded-full ${s.badge} text-white text-xs font-bold flex items-center justify-center`}>{i + 1}</span>
            <div className="min-w-0">
              <p className="text-[12px] font-bold text-dark leading-tight">{p.lead || "Key point"}</p>
              <p className="text-[11px] text-neutral leading-snug line-clamp-2">{p.text}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Face({ side, label, text, image, isPick, otherLabel, onFlip }) {
  const s = SIDE[side];
  const { hook, bullets } = parseEssay(text);
  const rest = bullets.slice(3);
  return (
    <div
      className="absolute inset-0 flex flex-col bg-[#FCFBF7] dark:bg-[#161923] rounded-2xl overflow-hidden"
      style={{ backfaceVisibility: "hidden", WebkitBackfaceVisibility: "hidden", transform: side === "b" ? "rotateY(180deg)" : "none" }}
      data-testid={`essay-face-${side}`}
    >
      <div className={`flex items-center justify-between gap-2 px-5 sm:px-6 pt-5 pb-3 border-b ${s.edge} border-opacity-30`}>
        <span className={`text-sm font-bold uppercase tracking-wider ${s.label}`}>{label}</span>
        {isPick && <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full ${s.pill}`}><Check className="w-3 h-3" /> YOUR PICK</span>}
      </div>

      <div className="flex-1 overflow-y-auto px-5 sm:px-6 py-4" data-testid={`essay-scroll-${side}`}>
        <SideInfographic side={side} label={label} hook={hook} bullets={bullets} image={image} />
        {rest.length > 0 && (
          <ul className="space-y-2.5">
            {rest.map((b, i) => (
              <li key={`r-${i}`} className="flex gap-2.5 text-[13.5px] leading-6 text-dark/80">
                <span className={`mt-[7px] w-1.5 h-1.5 rounded-full shrink-0 ${s.dot}`} />
                <span>{b.lead && <strong className="text-dark">{b.lead}: </strong>}{b.text}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="px-5 sm:px-6 py-3 border-t border-stone-200 dark:border-[#262B38] bg-white/60 dark:bg-white/[0.04] backdrop-blur-sm">
        <button
          onClick={onFlip}
          className={`w-full inline-flex items-center justify-center gap-2 text-sm font-semibold text-white py-2.5 rounded-xl shadow-sm active:scale-[0.98] transition-transform ${SIDE[side === "a" ? "b" : "a"].btn}`}
          data-testid={`flip-to-${side === "a" ? "b" : "a"}-btn`}
        >
          <RefreshCcw className="w-4 h-4" /> Flip to {otherLabel}
        </button>
      </div>
    </div>
  );
}

export default function DebateEssays({ topic, topicId, api, userVote }) {
  const [essays, setEssays] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [focus, setFocus] = useState(userVote || "a");
  const [collapsed, setCollapsed] = useState(() => (typeof window !== "undefined" ? window.innerWidth < 768 : false));
  const touchX = useRef(null);
  const reduced = useReducedMotion();

  const load = useCallback(async () => {
    setLoading(true); setError(false);
    try {
      const { data } = await api.get(`/topics/${topicId}/essays`, { timeout: 120000 });
      setEssays(data);
    } catch (err) {
      console.error("essay load failed", err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [api, topicId]);

  useEffect(() => { if (!collapsed) load(); }, [load, collapsed]);
  useEffect(() => { if (userVote) setFocus(userVote); }, [userVote]);

  const onTouchStart = (e) => { touchX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(dx) > 50) setFocus(dx < 0 ? "b" : "a");
    touchX.current = null;
  };
  const flip = () => setFocus((f) => (f === "a" ? "b" : "a"));

  const labelA = topic.side_a_label;
  const labelB = topic.side_b_label;

  return (
    <div className="bg-white rounded-2xl shadow-card p-6 sm:p-8 mb-4" data-testid="debate-essays">
      <button onClick={() => setCollapsed((c) => !c)} className="w-full flex items-center justify-between gap-2" data-testid="essays-collapse-toggle">
        <span className="flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-neutral" />
          <span className="font-heading text-xl text-dark">THE FULL DEBATE</span>
        </span>
        <span className="flex items-center gap-1.5 text-xs font-semibold text-neutral">
          {collapsed ? "Open" : "Collapse"}
          <ChevronDown className={`w-4 h-4 transition-transform ${collapsed ? "" : "rotate-180"}`} />
        </span>
      </button>

      {collapsed ? (
        <div className="mt-3 flex items-center gap-2 text-xs text-neutral" data-testid="essays-collapsed">
          <Sparkles className="w-4 h-4 text-view-a" /> In-depth arguments for both sides — tap "Open" to read.
        </div>
      ) : (
        <>
          {/* Segmented toggle */}
          <div className="mt-4 mb-4 relative flex p-1 rounded-xl bg-stone-100 dark:bg-[#1E2230]" data-testid="essay-side-toggle">
            <motion.div
              className={`absolute top-1 bottom-1 rounded-lg shadow-sm ${focus === "a" ? SIDE.a.btn : SIDE.b.btn}`}
              animate={{ left: focus === "a" ? "0.25rem" : "50%", right: focus === "a" ? "50%" : "0.25rem" }}
              transition={{ type: "spring", stiffness: 350, damping: 30 }}
            />
            {["a", "b"].map((sd) => (
              <button
                key={sd}
                onClick={() => setFocus(sd)}
                className={`relative z-10 flex-1 flex items-center justify-center gap-1.5 py-2 text-sm font-semibold rounded-lg transition-colors ${focus === sd ? "text-white" : "text-neutral"}`}
                data-testid={`select-side-${sd}`}
              >
                {(sd === "a" ? labelA : labelB)}
                {userVote === sd && <Check className="w-3.5 h-3.5" />}
              </button>
            ))}
          </div>

          <p className="text-xs text-neutral mb-4" data-testid="essay-hint">
            {userVote
              ? "Your side is showing. Flip the card, tap the other tab, or swipe to read the opposing view."
              : "Tap a tab, hit flip, or swipe to turn the card and weigh both sides."}
          </p>

          {loading && (
            <div className="flex flex-col items-center justify-center py-14 text-center" data-testid="essay-loading">
              <Loader2 className="w-7 h-7 text-view-a animate-spin mb-3" />
              <p className="text-sm font-medium text-dark">Crafting both sides &amp; illustrations…</p>
              <p className="text-xs text-neutral mt-1">This happens once per debate, then it's instant.</p>
            </div>
          )}

          {error && !loading && (
            <div className="text-center py-10">
              <p className="text-sm text-neutral mb-3">Couldn't load the full arguments.</p>
              <button onClick={load} className="inline-flex items-center gap-1.5 text-sm font-semibold text-view-a" data-testid="essay-retry-btn">
                <RefreshCw className="w-4 h-4" /> Try again
              </button>
            </div>
          )}

          {essays && !loading && (
            <div style={{ perspective: 2200 }} data-testid="debate-book" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
              <motion.div
                className="relative w-full h-[540px] sm:h-[620px]"
                style={{ transformStyle: "preserve-3d" }}
                animate={reduced ? {} : { rotateY: focus === "a" ? 0 : 180 }}
                transition={{ type: "spring", stiffness: 70, damping: 15 }}
              >
                <div className="absolute inset-0 rounded-2xl shadow-[0_18px_50px_rgba(0,0,0,0.12)]" />
                <Face side="a" label={labelA} text={essays.side_a_essay} image={undefined} isPick={userVote === "a"} otherLabel={labelB} onFlip={flip} />
                <Face side="b" label={labelB} text={essays.side_b_essay} image={undefined} isPick={userVote === "b"} otherLabel={labelA} onFlip={flip} />
              </motion.div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
